import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import APIUtil from 'util/APIUtil';

import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import type {
  HomepageV2TemplatesQuery,
  HomepageV2TemplatesQueryVariables,
  HomepageV2TemplatesQuery_subtemplatesByIds_SubtemplateNode as SubtemplateNode,
  HomepageV2TemplatesQuery_templatesByIds_TemplateNode as TemplateNode,
} from './__generated/HomepageV2TemplatesQuery';
import {MyWorkCategory} from './MyWorkConstants';
import {reviewableRowProps} from './MyWorkRowHelpers';
import {MyWorkRowsFooterAction, MyWorkRowsList} from './MyWorkRowsList';
import {useMyWorkRows} from './useMyWorkRows';

type HomepageTemplatesRowItem = TemplateNode | SubtemplateNode;

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

export const HOMEPAGE_TEMPLATES_QUERY = gql`
  query HomepageV2TemplatesQuery($templateIds: [StrictString]!, $subtemplateIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NAVIGATION) {
    templatesByIds(ids: $templateIds) {
      ... on TemplateNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
    subtemplatesByIds(ids: $subtemplateIds) {
      ... on SubtemplateNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

/** Fetches and lists entry templates and sub-templates for the selected review-status filter. */
export default function TemplatesRows({
  footerAction,
  hasCategoryWork,
  selectedTemplateIds,
}: {
  footerAction?: MyWorkRowsFooterAction;
  hasCategoryWork: boolean;
  selectedTemplateIds: Array<string> | null;
}): JSX.Element {
  const [subtemplateIds, templateIds] = _.partition(selectedTemplateIds ?? [], (id) =>
    APIUtil.isSubtemplate(id)
  );

  const rowsResult = useMyWorkRows<
    HomepageV2TemplatesQuery,
    HomepageV2TemplatesQueryVariables,
    HomepageTemplatesRowItem
  >({
    query: HOMEPAGE_TEMPLATES_QUERY,
    variables: {templateIds, subtemplateIds},
    skip: selectedTemplateIds == null,
    getItems: (data) => [...data.templatesByIds, ...data.subtemplatesByIds],
    toRowProps: (item) => reviewableRowProps(item),
    errorMessage: 'Error loading templates',
    testComponent: TEST_COMPONENT,
  });

  if (rowsResult.type !== QueryResultType.SUCCESS) {
    return rowsResult.element;
  }

  return (
    <MyWorkRowsList
      rows={rowsResult.rows}
      category={MyWorkCategory.TEMPLATES}
      hasCategoryWork={hasCategoryWork}
      footerAction={footerAction}
      emptyMessageDataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'messageRow',
        key: 'emptyTemplates',
      }}
    />
  );
}
